import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { kidsApi } from '@/services/kids.api';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

export function CheckoutDialog({
  checkInId,
  childDisplayName,
  onClose,
  onCheckedOut,
}: {
  checkInId: string;
  childDisplayName: string;
  onClose: () => void;
  onCheckedOut: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [pickedUpById, setPickedUpById] = useState<string | null>(null);
  const [pickedUpByLabel, setPickedUpByLabel] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const guardiansQuery = useQuery({
    queryKey: ['kids', 'guardians', checkInId],
    queryFn: ({ signal }) => kidsApi.guardians(checkInId, signal),
  });

  const mutation = useMutation({
    mutationFn: () => kidsApi.checkOut(checkInId, code.trim(), pickedUpById),
    onSuccess: onCheckedOut,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const guardians = guardiansQuery.data ?? [];

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="check-out-title">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="check-out-title">
          {t('kids.checkin.checkOutFor', { values: { child: childDisplayName } })}
        </h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        {guardians.length > 0 && (
          <p className="auth-subtitle">
            {t('kids.checkin.registeredGuardians', {
              values: { names: guardians.map((g) => g.displayName).join(', ') },
            })}
          </p>
        )}

        <Field
          label={t('kids.checkin.code')}
          name="code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          inputMode="numeric"
          dir="ltr"
          autoFocus
          required
        />

        <PersonPicker
          label={t('kids.checkin.guardianPickup')}
          value={pickedUpByLabel}
          selectedId={pickedUpById}
          onChange={setPickedUpByLabel}
          onPick={(id, label) => {
            setPickedUpById(id);
            setPickedUpByLabel(label);
          }}
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={code.trim().length < 4}
            loading={mutation.isPending}
          >
            {t('kids.checkin.checkOut')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function PersonPicker({
  label,
  value,
  selectedId,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  selectedId: string | null;
  onChange: (value: string) => void;
  onPick: (id: string, label: string) => void;
}) {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ['people', 'picker', value],
    queryFn: ({ signal }) => peopleApi.list({ search: value, limit: 8 }, signal),
    enabled: value.trim().length >= 2 && selectedId === null,
  });
  const candidates = (query.data?.data ?? []).filter((person) => !person.archivedAt);

  return (
    <div className="member-picker">
      <Field
        label={label}
        name="pickup-search"
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('events.registration.choosePerson')}
      />
      {candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((person) => (
            <li key={person.id}>
              <Button variant="ghost" onClick={() => onPick(person.id, person.displayName)}>
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
